import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { resolveFeatureFlags, type FeatureFlagResolveResult } from '@/shared/data/featureFlags';
import { queryKeys } from '@/shared/hooks';
import { hashString } from '@/shared/utils/hash';

type FeatureFlagsCacheEntry = { flags: FeatureFlagResolveResult['flags']; keys: Set<string> };

const featureFlagsCache = new Map<string, FeatureFlagsCacheEntry>();
const shouldBypassFlags = import.meta.env.VITE_E2E_FLAGS_BYPASS === 'true';

type UseFeatureFlagsArgs = {
  keys: string[];
  token?: string | null;
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
  staleTimeMs?: number;
};

export const useFeatureFlags = ({
  keys,
  token,
  enabled = true,
  refetchOnWindowFocus = true,
  staleTimeMs,
}: UseFeatureFlagsArgs): {
  flags: FeatureFlagResolveResult['flags'] | null;
  isLoading: boolean;
} => {
  const bypassedFlags = useMemo(() => {
    if (!shouldBypassFlags) return null;
    const resolved: FeatureFlagResolveResult['flags'] = {};
    keys.forEach((key) => {
      resolved[key] = true;
    });
    return resolved;
  }, [keys]);
  if (bypassedFlags) {
    return { flags: bypassedFlags, isLoading: false };
  }
  const keysKey = useMemo(() => {
    if (keys.length === 0) return null;
    const sortedKeys = [...keys].sort();
    return JSON.stringify(sortedKeys);
  }, [keys]);
  const tokenKey = useMemo(() => {
    if (!token) return null;
    return hashString(token);
  }, [token]);
  const query = useQuery({
    queryKey: queryKeys.featureFlagResolve(keysKey, tokenKey),
    queryFn: async () => {
      const cacheKey = `global:${tokenKey ?? 'anonymous'}`;
      const cached = featureFlagsCache.get(cacheKey);
      if (cached) {
        const hasAllKeys = keys.every((key) => cached.keys.has(key));
        if (hasAllKeys) {
          return { flags: { ...cached.flags } };
        }
      }
      const result = await resolveFeatureFlags(keys, token);
      const existingFlags = cached?.flags ?? {};
      const mergedFlags = { ...existingFlags, ...result.flags };
      const mergedKeys = new Set(cached?.keys ?? []);
      keys.forEach((key) => mergedKeys.add(key));
      featureFlagsCache.set(cacheKey, { flags: mergedFlags, keys: mergedKeys });
      return { flags: mergedFlags };
    },
    enabled: enabled && Boolean(token) && keys.length > 0,
    refetchOnWindowFocus,
    staleTime: staleTimeMs ?? 300_000,
  });
  const flags = useMemo<FeatureFlagResolveResult['flags'] | null>(() => {
    if (!token) return null;
    if (query.isError) return null;
    return query.data?.flags ?? {};
  }, [query.data?.flags, query.isError, token]);

  return { flags, isLoading: query.isLoading };
};
